/* eslint-disable */
import axios from 'axios';
import secureLocalStorage from 'react-secure-storage';
import Swal from 'sweetalert2';
import { logger } from 'utils/environment/log';
import { configuration } from 'utils/environment/configuration';

export const BASE_URL = configuration.apiUrl;

// Store Keycloak instance
let keycloakInstance = null;

export const setKeycloakInstance = (instance) => {
    keycloakInstance = instance;
};

class APIError extends Error {
    constructor(message, statusCode, errors = null) {
        super(message);
        this.name = '';
        this.statusCode = statusCode;
        this.errors = errors;
    }
}

const axiosServices = axios.create({
    baseURL: BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
        Expires: '0'
    }
});

const addTimestamp = (config) => {
    const timestamp = new Date().getTime();
    const configWithTimestamp = { ...config };

    if (config.method?.toLowerCase() === 'get') {
        configWithTimestamp.params = {
            ...configWithTimestamp.params,
            _t: timestamp
        };
    }

    return configWithTimestamp;
};

axiosServices.interceptors.request.use(
    async (config) => {
        try {
            const token = secureLocalStorage.getItem('token');

            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }

            const configWithTimestamp = addTimestamp(config);

            configWithTimestamp.headers = {
                ...configWithTimestamp.headers,
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                Pragma: 'no-cache',
                Expires: '0'
            };

            if (config.url?.endsWith('/q')) {
                if (config.method === 'post' && config.params) {
                    const { page = 0, size = 20 } = config.params;
                    configWithTimestamp.params = {
                        ...configWithTimestamp.params,
                        page,
                        size
                    };

                    if (configWithTimestamp.data && !Array.isArray(configWithTimestamp.data)) {
                        configWithTimestamp.data = [configWithTimestamp.data];
                    }

                    if (Array.isArray(configWithTimestamp.data)) {
                        configWithTimestamp.data = configWithTimestamp.data.map((criteria) => ({
                            key: criteria.key || 'string',
                            value: criteria.value || {},
                            operation: criteria.operation || 'GREATER_THAN',
                            orPredicate: criteria.orPredicate ?? true
                        }));
                    }
                }

                configWithTimestamp.headers = {
                    ...configWithTimestamp.headers,
                    'Content-Type': 'application/json',
                    Accept: 'application/json'
                };
            }

            if (configuration.isDevelopment === 'development') {
                logger.log('Request Config:', {
                    url: configWithTimestamp.url,
                    method: configWithTimestamp.method,
                    params: configWithTimestamp.params,
                    headers: configWithTimestamp.headers,
                    data: configWithTimestamp.data
                });
            }

            return configWithTimestamp;
        } catch (error) {
            logger.error('Request configuration failed:', error);
            return Promise.reject(new APIError('Authentication failed. Please login again.', 401));
        }
    },
    (error) => Promise.reject(new APIError(error.message || 'Request configuration failed', 500))
);

axiosServices.interceptors.response.use(
    (response) => response,
    async (error) => {
        const { response } = error;


        if (response) {
            const { status, data } = response;
            const message = data?.detail || data?.message || 'An error occurred';
            logger.error('API Error:', { status, message, data });

            switch (status) {
                case 401:
                    if (keycloakInstance) {
                        try {
                            const result = await Swal.fire({
                                title: 'Session Expired',
                                text: 'Your session has expired. Would you like to refresh your session or logout?',
                                icon: 'warning',
                                showDenyButton: true,
                                showCancelButton: false,
                                confirmButtonText: 'Refresh Session',
                                denyButtonText: 'Logout',
                                confirmButtonColor: '#1C4B45',
                                denyButtonColor: '#d33',
                                allowOutsideClick: false
                            });

                            if (result.isConfirmed) {
                                try {
                                    const refreshed = await keycloakInstance.updateToken(30);
                                    if (refreshed) {
                                        secureLocalStorage.setItem('token', keycloakInstance.token);
                                        error.config.headers.Authorization = `Bearer ${keycloakInstance.token}`;
                                        return axios(error.config);
                                    }
                                } catch (refreshError) {
                                    logger.error('Token refresh failed:', refreshError);
                                    await keycloakInstance.logout();
                                    return Promise.reject(new APIError('Session refresh failed', status));
                                }
                            } else if (result.isDenied) {
                                await keycloakInstance.logout();
                                return Promise.reject(new APIError('User logged out', status));
                            }
                        } catch (e) {
                            logger.error('Error handling session expiration:', e);
                            await keycloakInstance.logout();
                            return Promise.reject(new APIError('Authentication error', status));
                        }
                    }
                    return Promise.reject(new APIError('Session expired. Please login again.', status));

                case 400:
                    Swal.fire({
                        title: 'Invalid Request',
                        text: message,
                        icon: 'error'
                    });
                    return Promise.reject(new APIError(message, status, data.errors));

                case 403:
                    Swal.fire({
                        title: 'Access Denied',
                        text: 'You do not have permission to perform this action.',
                        icon: 'error'
                    });
                    return Promise.reject(new APIError('Access denied', status));

                case 404:
                    Swal.fire({
                        title: 'Not Found',
                        text: message,
                        icon: 'error'
                    });
                    return Promise.reject(new APIError(message, status));

                case 406:
                    Swal.fire({
                        title: 'Not Acceptable',
                        text: 'The requested format is not supported.',
                        icon: 'error'
                    });
                    return Promise.reject(new APIError('Not acceptable', status));
                case 409:
                    Swal.fire({
                        title: 'Conflict',
                        text: message || 'A conflict occurred with the current state of the resource.',
                        icon: 'warning'
                    });
                    return Promise.reject(new APIError('Resource conflict', status));

                case 413:
                    Swal.fire({
                        title: 'Request Too Large',
                        text: 'The request is too large to process.',
                        icon: 'error'
                    });
                    return Promise.reject(new APIError('Payload too large', status));

                case 408:
                    // Swal.fire({
                    //     title: 'Request Timeout',
                    //     text: 'The request took too long to complete.',
                    //     icon: 'error'
                    // });
                    return Promise.reject(new APIError('Request timeout', status));

                case 422:
                    Swal.fire({
                        title: 'Validation Error',
                        text: message,
                        icon: 'error'
                    });
                    return Promise.reject(new APIError(message, status, data.errors));

                case 500:
                    // Swal.fire({
                    //     title: 'Server Error',
                    //     text: 'An unexpected error occurred. Please try again later.',
                    //     icon: 'error'
                    // });
                    return Promise.reject(new APIError('Internal server error', status));
                case 501:
                    // Swal.fire({
                    //     title: 'Not Implemented',
                    //     text: 'This functionality is not available.',
                    //     icon: 'error'
                    // });
                    return Promise.reject(new APIError('Not implemented', status));

                case 502:
                    // Swal.fire({
                    //     title: 'Bad Gateway',
                    //     text: 'The server received an invalid response.',
                    //     icon: 'error'
                    // });
                    return Promise.reject(new APIError('Bad gateway', status));

                case 503:
                    // Swal.fire({
                    //     title: 'Service Unavailable',
                    //     text: 'The service is temporarily unavailable. Please try again later.',
                    //     icon: 'error'
                    // });
                    return Promise.reject(new APIError('Service unavailable', status));

                case 504:
                    // Swal.fire({
                    //     title: 'Gateway Timeout',
                    //     text: 'The server took too long to respond.',
                    //     icon: 'error'
                    // });
                    return Promise.reject(new APIError('Gateway timeout', status));

                default:
                    return Promise.reject(new APIError(message, status));
            }
        }

        return Promise.reject(new APIError(error.message || 'An unexpected error occurred', 500));
    }
);

export const apiCaller = {
    get: async (url, config = {}) => {
        try {
            const response = await axiosServices.get(url, config);
            return response.data;
        } catch (error) {
            if (error instanceof APIError) throw error;
            throw new APIError(error.message || 'GET request failed', 500);
        }
    },

    post: async (url, data = {}, config = {}) => {
        try {
            const response = await axiosServices.post(url, data, config);
            return response.data;
        } catch (error) {
            console.log(`-=-==222222=2=2=2=2=2=2`,error)
            if (error instanceof APIError) throw error;
            throw new APIError(error.message || 'POST request failed', 500);
        }
    },

    put: async (url, data = {}, config = {}) => {
        try {
            const response = await axiosServices.put(url, data, config);
            return response.data;
        } catch (error) {
            if (error instanceof APIError) throw error;
            throw new APIError(error.message || 'PUT request failed', 500);
        }
    },

    delete: async (url, config = {}) => {
        try {
            const response = await axiosServices.delete(url, config);
            return response.data;
        } catch (error) {
            if (error instanceof APIError) throw error;
            throw new APIError(error.message || 'DELETE request failed', 500);
        }
    },

    postFormData: async (url, data = {}, config = {}) => {
        try {
            const response = await fetch(`${BASE_URL}${url}`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${secureLocalStorage.getItem('token')}`
                },
                body: data
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Request failed');
            }

            return await response.json();
        } catch (error) {
            logger.error('Form data upload failed:', error);
            throw error;
        }
    },
    postFormDatai: async (url, data = {}, config = {}) => {
        try {
            const response = await fetch(`${BASE_URL}${url}`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${secureLocalStorage.getItem('token')}`
                },
                body: data
            });


            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Request failed');
            }

            const jsonResponse = await response.json();
            return jsonResponse;
        } catch (error) {
            logger.log(error);
            throw error;
        }
    },
    search: async (url, searchCriteria = [], pageable = {}) => {
        try {
            const formattedPageable = {
                page: pageable.page || 0,
                size: pageable.size || 10
            };

            const formattedCriteria = searchCriteria.map((criteria) => ({
                key: criteria.key,
                value: criteria.value,
                operation: criteria.operation || 'GREATER_THAN',
                orPredicate: criteria.orPredicate ?? true
            }));

            const response = await axiosServices.post(`${url}/q`, formattedCriteria, {
                params: formattedPageable
            });

            return response.data;
        } catch (error) {
            if (error instanceof APIError) throw error;
            throw new APIError(error.message || 'Search request failed', 500);
        }
    },

    list: async (url, page = 0, size = 10, config = {}) => {
        try {
            const response = await axiosServices.get(url, {
                ...config,
                params: {
                    ...config.params,
                    page,
                    size
                }
            });
            return response.data;
        } catch (error) {
            if (error instanceof APIError) throw error;
            throw new APIError(error.message || 'List request failed', 500);
        }
    }
};

export const createSearchCriteria = ({ key, value, operation = 'GREATER_THAN', orPredicate = false }) => ({
    key,
    value,
    operation,
    orPredicate
});

export const createPageable = ({ page = 0, size = 10 }) => ({
    page,
    size
});

export const isAPIError = (error) => error instanceof APIError;

export { APIError };
export default axiosServices;
